import { useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { ITextTemplate } from "../../../App/Typings/MessageText";
import { useUpsertMessageTemplate } from "../../../App/Queries/MessageQueries";
import { InputTextBody, InputName, InputVisibility } from "../Shared/TemplateInputs";
import { selectDraftModalState, useDraftMessageTemplateStore } from "../Shared/useMessageTemplateStore";

export const DraftTextTemplateModal = ({
  isDraftDialogOpen,
  setIsDraftDialogOpen,
  action,
  editTemplate,
  setEditTemplate,
}: {
  isDraftDialogOpen: boolean;
  setIsDraftDialogOpen: (value: boolean) => void;
  action: "create" | "edit" | "duplicate";
  editTemplate: ITextTemplate | null;
  setEditTemplate: (value: ITextTemplate | null) => void;
}) => {
  const handleCloseModal = () => {
    setIsDraftDialogOpen(false);
  };
  return (
    <Dialog
      open={isDraftDialogOpen}
      fullWidth
      maxWidth="lg"
      onClose={handleCloseModal}
      data-test={`${action}-modal`}
    >
      {isDraftDialogOpen && (
        <DraftTextTemplateContent
          action={action}
          setIsDraftDialogOpen={setIsDraftDialogOpen}
          editTemplate={editTemplate}
          setEditTemplate={setEditTemplate}
        />
      )}
    </Dialog>
  );
};

const DraftTextTemplateContent = ({
  action,
  setIsDraftDialogOpen,
  editTemplate,
  setEditTemplate,
}: {
  action: "create" | "edit" | "duplicate";
  setIsDraftDialogOpen: (value: boolean) => void;
  editTemplate: ITextTemplate | null;
  setEditTemplate: (value: ITextTemplate | null) => void;
}) => {
  const { disableSaveMessage, clearDraft, createUpsertCommand, createDraft, createDuplicate, setType } =
    useDraftMessageTemplateStore(selectDraftModalState);
  const { mutateAsync: upsertMessageTemplate, isLoading: isLoadingUpsertMessageTemplate } =
    useUpsertMessageTemplate();

  const actionTitle = `${action[0].toUpperCase() + action.slice(1)} Text Template`;
  const actionId = `${action}-text-template`;

  const handleCloseModal = () => {
    setIsDraftDialogOpen(false);
    setTimeout(() => {
      clearDraft();
      setEditTemplate(null);
    }, 300);
  };

  const handleSaveCurrentTemplate = async () => {
    if (!disableSaveMessage) {
      await upsertMessageTemplate(createUpsertCommand());
      handleCloseModal();
    }
  };

  useEffect(() => {
    setType("text");
    if (editTemplate && action === "edit") {
      createDraft(editTemplate);
    } else if (editTemplate && action === "duplicate") {
      createDuplicate(editTemplate);
    } else if (action === "create") {
      createDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <DialogTitle data-test={`${actionId}-title`}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span id={`${actionId}-label`}>{actionTitle}</span>
          <IconButton
            data-test={`close-${actionId}-modal`}
            aria-label="Close Dialog"
            onClick={handleCloseModal}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider sx={{ maxWidth: "calc(100% - 48px)", mx: "auto", my: 0, width: 1 }} />
      <DialogContent sx={{ pt: "1rem" }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, mb: 2 }}>
          <Box sx={{ flex: 2 }}>
            <InputName initialValue={editTemplate?.name ?? ""} isLoading={isLoadingUpsertMessageTemplate} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <InputVisibility isLoading={isLoadingUpsertMessageTemplate} />
          </Box>
        </Box>
        <Box>
          <InputTextBody initialValue={editTemplate?.body ?? ""} isLoading={isLoadingUpsertMessageTemplate} />
        </Box>
      </DialogContent>
      <Divider sx={{ maxWidth: "calc(100% - 48px)", mx: "auto", my: 0, width: 1 }} />
      <DialogActions sx={{ paddingRight: "1rem", paddingBottom: "1rem" }}>
        <Tooltip title={disableSaveMessage ?? ""} arrow>
          <Box>
            <Button
              variant="contained"
              id="save-template-button"
              aria-haspopup="true"
              disableElevation
              disabled={!!disableSaveMessage || isLoadingUpsertMessageTemplate}
              data-test="save-template"
              onClick={handleSaveCurrentTemplate}
            >
              Save
            </Button>
          </Box>
        </Tooltip>
      </DialogActions>
    </>
  );
};
